import { useMemo } from 'react';
import { FilterConfigsDto } from '@asset-metrix/portal-gateway-client-js';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { useSelections } from '../store/hooks/useSelections';
import { type FilterSelections } from '../store/states/filterSelection';
import { FilterConfigDto } from '../store/states/filterTypes';
import { useStakeAdjustmentStore } from '../store/states/stakeAdjustment';
import { GLOBAL_SEARCH_URL_PARAM } from '../utils/constants';
import { getTableFilterInstanceId } from '../utils/filterInstanceId';
import {
  selectionsToKeyValuePairs,
  selectionToKeyValuePairs,
} from '../utils/filtersToKeyValuePairs';
import { createGlobalFilterSearchParamKey } from '../utils/globalSearchHelpers';
import {
  getAllTableWidgets,
  getVisibleWidgetIds,
  type TableWidget,
} from '../utils/pageContentHelpers';

import { createTableFilterSearchParamKey } from './usePresetTableFiltersFromUrl';
import {
  UseSyncFiltersToSearchParams,
  useSyncFiltersToSearchParams,
} from './useSyncFiltersToSearchParams';

function isGlobalSearchConfig(config: FilterConfigDto): boolean {
  return config.type === 'text' && config.isGlobalSearchFilter === true;
}

/**
 * Resolves the global search value from the currently visible widget only.
 * Each content group tab keeps its own independent search value — inactive
 * tabs are ignored so the URL reflects only the active tab's state.
 */
function resolveGlobalSearchValue(
  tableWidgets: TableWidget[],
  filterSelections: Record<string, FilterSelections>,
  visibleWidgetIds: Set<string>,
  pageFilterInstanceId: string
): string | null {
  for (const widget of tableWidgets) {
    if (!visibleWidgetIds.has(widget.id)) {
      continue;
    }
    const configs = widget.entity.config.filters;
    if (configs == null) {
      continue;
    }
    const selections =
      filterSelections[
        getTableFilterInstanceId(widget.id, pageFilterInstanceId)
      ] ?? {};
    for (const config of configs) {
      if (!isGlobalSearchConfig(config)) {
        continue;
      }
      const selection = selections[config.key];
      if (selection != null && selection !== '') {
        return String(selection);
      }
    }
  }
  return null;
}

export const STAKE_ADJUSTED_URL_KEY = 'stakeAdjusted';

type UseSyncAllFiltersToSearchParams = {
  globalFilterConfig?: FilterConfigDto[];
  globalFilterInstanceId: string | null;
  pageFilterConfig?: FilterConfigsDto;
  pageFilterInstanceId: string | null;
  /** boolean to determine if page has any stake adjustment content (also checked across all group tabs) */
  pageHasAnyStakeAdjustment?: boolean;
  enableStakeAdjustmentByDefault?: boolean;
  /** Map of tabGroupId → active tab value, used to prefer the visible widget's
   *  global search value over stale values from inactive tabs. */
  activeTabValues?: Record<string, string>;
} & Pick<
  UseSyncFiltersToSearchParams,
  'navigationData' | 'navItemId' | 'pageData'
>;

const EMPTY_KEYS: string[] = [];

const EMPTY_ACTIVE_TAB_VALUES: Record<string, string> = {};

export function useSyncAllFiltersToSearchParams({
  globalFilterConfig,
  globalFilterInstanceId,
  pageFilterConfig,
  pageFilterInstanceId,
  navigationData,
  navItemId,
  pageData,
  pageHasAnyStakeAdjustment,
  enableStakeAdjustmentByDefault,
  activeTabValues = EMPTY_ACTIVE_TAB_VALUES,
}: UseSyncAllFiltersToSearchParams) {
  const globalSelections = useSelections(globalFilterInstanceId ?? '');
  const globalIsInitialized = useStore(
    (state) => state.filterInitialized[globalFilterInstanceId ?? ''] ?? false
  );

  const pageSelections = useSelections(pageFilterInstanceId ?? '');
  const pageIsInitialized = useStore(
    (state) => state.filterInitialized[pageFilterInstanceId ?? ''] ?? false
  );

  const tableWidgets = useMemo(
    () => getAllTableWidgets(pageData?.contents ?? []),
    [pageData?.contents]
  );

  const filterInitialized = useStore(
    useShallow((state) => state.filterInitialized)
  );

  // Subscribe reactively so table filter selection changes trigger URL sync.
  const filterSelections = useStore(
    useShallow((state) => state.filterSelections)
  );

  const stakeAdjusted = useStakeAdjustmentStore((state) =>
    pageFilterInstanceId ? state.stakeAdjusted[pageFilterInstanceId] : undefined
  );

  const visibleWidgetIds = useMemo(
    () => getVisibleWidgetIds(pageData?.contents ?? [], activeTabValues),
    [pageData?.contents, activeTabValues]
  );

  // Wait until every filter level that will exist is initialized before
  // syncing anything to the URL.  This avoids partial writes that would strip
  // search params belonging to a not-yet-initialized level.
  const allInitialized = useMemo(() => {
    const globalReady =
      globalFilterConfig == null ||
      globalFilterInstanceId == null ||
      globalIsInitialized;
    const pageReady =
      pageFilterConfig == null ||
      pageFilterInstanceId == null ||
      pageIsInitialized;
    const tablesReady = tableWidgets.every((widget) => {
      const configs = widget.entity.config.filters;
      const hasFilterConfigs = configs != null && configs.length > 0;
      if (!hasFilterConfigs) {
        return true;
      }
      if (pageFilterInstanceId == null) {
        return false;
      }
      return (
        filterInitialized[
          getTableFilterInstanceId(widget.id, pageFilterInstanceId)
        ] ?? false
      );
    });
    const stakeAdjustmentReady =
      !pageHasAnyStakeAdjustment || stakeAdjusted !== undefined;

    return globalReady && pageReady && tablesReady && stakeAdjustmentReady;
  }, [
    globalFilterConfig,
    globalFilterInstanceId,
    globalIsInitialized,
    pageFilterConfig,
    pageFilterInstanceId,
    pageIsInitialized,
    tableWidgets,
    filterInitialized,
    pageHasAnyStakeAdjustment,
    stakeAdjusted,
  ]);

  const filtersToSet = useMemo((): [string, string][] | null => {
    if (!allInitialized) {
      return null;
    }

    const pairs: [string, string][] = [];

    if (globalFilterConfig != null && globalFilterInstanceId != null) {
      pairs.push(
        ...selectionsToKeyValuePairs(
          globalSelections,
          globalFilterConfig,
          createGlobalFilterSearchParamKey
        )
      );
    }

    if (pageFilterConfig != null && pageFilterInstanceId != null) {
      pairs.push(
        ...selectionsToKeyValuePairs(pageSelections, pageFilterConfig.filters)
      );
    }

    // Regular tf_ pairs — only for visible (active-tab) widgets so that
    // switching tabs removes stale tf_ params from the URL.
    if (pageFilterInstanceId != null) {
      for (const widget of tableWidgets) {
        if (!visibleWidgetIds.has(widget.id)) {
          continue;
        }
        const configs = widget.entity.config.filters;
        if (configs == null) {
          continue;
        }
        const selections =
          filterSelections[
            getTableFilterInstanceId(widget.id, pageFilterInstanceId)
          ] ?? {};
        for (const config of configs) {
          if (isGlobalSearchConfig(config)) {
            continue;
          }
          const selection = selections[config.key];
          if (selection != null) {
            pairs.push(
              ...selectionToKeyValuePairs(
                createTableFilterSearchParamKey(widget.id, config.key),
                selection
              )
            );
          }
        }
      }

      // Global search — only the visible (active-tab) widget's value is used.
      const globalSearch = resolveGlobalSearchValue(
        tableWidgets,
        filterSelections,
        visibleWidgetIds,
        pageFilterInstanceId
      );
      if (globalSearch != null) {
        pairs.push(
          ...selectionToKeyValuePairs(GLOBAL_SEARCH_URL_PARAM, globalSearch)
        );
      }
    }

    if (
      pageHasAnyStakeAdjustment &&
      stakeAdjusted != null &&
      stakeAdjusted !== (enableStakeAdjustmentByDefault ?? false)
    ) {
      pairs.push([STAKE_ADJUSTED_URL_KEY, String(stakeAdjusted)]);
    }

    return pairs;
  }, [
    allInitialized,
    globalFilterConfig,
    globalFilterInstanceId,
    globalSelections,
    pageFilterConfig,
    pageFilterInstanceId,
    pageSelections,
    tableWidgets,
    filterSelections,
    visibleWidgetIds,
    pageHasAnyStakeAdjustment,
    stakeAdjusted,
    enableStakeAdjustmentByDefault,
  ]);

  const allFilterUrlKeys = useMemo(() => {
    const keys = new Set<string>();

    if (
      globalIsInitialized &&
      globalFilterConfig != null &&
      globalFilterInstanceId != null
    ) {
      for (const fc of globalFilterConfig) {
        keys.add(createGlobalFilterSearchParamKey(fc.key));
      }
    }

    if (
      pageIsInitialized &&
      pageFilterConfig != null &&
      pageFilterInstanceId != null
    ) {
      for (const fc of pageFilterConfig.filters) {
        keys.add(fc.key);
      }
    }

    for (const widget of tableWidgets) {
      const configs = widget.entity.config.filters;
      if (pageFilterInstanceId == null || configs == null) {
        continue;
      }
      const tableFilterInstanceId = getTableFilterInstanceId(
        widget.id,
        pageFilterInstanceId
      );

      if (!filterInitialized[tableFilterInstanceId]) {
        continue;
      }

      for (const fc of configs) {
        if (isGlobalSearchConfig(fc)) {
          keys.add(GLOBAL_SEARCH_URL_PARAM);
        }
        keys.add(createTableFilterSearchParamKey(widget.id, fc.key));
      }
    }

    if (pageHasAnyStakeAdjustment) {
      keys.add(STAKE_ADJUSTED_URL_KEY);
    }

    const result = [...keys];
    return result.length > 0 ? result : EMPTY_KEYS;
  }, [
    globalIsInitialized,
    globalFilterConfig,
    globalFilterInstanceId,
    pageIsInitialized,
    pageFilterConfig,
    pageFilterInstanceId,
    tableWidgets,
    filterInitialized,
    pageHasAnyStakeAdjustment,
  ]);

  useSyncFiltersToSearchParams({
    filtersToSet,
    allFilterUrlKeys,
    navigationData,
    navItemId,
    pageData,
    pageFilterInstanceId,
  });
}
